import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Download,
  User,
  Copy,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useDebounce } from '../../hooks/useDebounce.js';
import { api, ClientFilters } from '../../lib/api';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { CreateClientModal } from '../../components/cashier/CreateClientModal';

export default function CashierClientsPage() {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [filters, setFilters] = useState<ClientFilters>({
    page: 1,
    limit: 20,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', currentEntity?.id, filters, debouncedSearch],
    queryFn: () => api.clients.list(currentEntity!.id, { ...filters, search: debouncedSearch?.trim() ? debouncedSearch.trim().toLowerCase() : undefined }),
    enabled: !!currentEntity,
  });

  const handleCopyId = useCallback((id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success(t('cashier.clients.idCopied', 'ID copied to clipboard'));
    setTimeout(() => setCopiedId(null), 2000);
  }, [toast, t]);

  const handleExport = () => {
    if (!data?.data?.length) return;

    const headers = ['ID', 'Client Name', 'Alias', 'Employee', 'Transactions', 'First Seen'];
    const rows = data.data.map((client) => [
      client.id,
      client.name,
      client.uniqueId,
      client.isEmployee ? 'yes' : '',
      client.transactionCount || 0,
      formatDate(client.createdAt),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients-${currentEntity?.name || 'export'}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!currentEntity) return null;

  const totalPages = data?.meta?.totalPages || 1;
  const currentPage = filters.page || 1;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-center flex-col md:flex-row gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-bold text-foreground m-0">{t('cashier.clients.title', 'Clients')}</h1>
            <p className="text-sm text-foreground-muted m-0">{t('cashier.clients.subtitle', 'Manage users and contacts')}</p>
          </div>
          <div className="flex gap-3">
            <button
              className="flex items-center gap-2 px-5 py-2.5 bg-ctp-blue text-white border-none rounded-lg text-sm font-semibold cursor-pointer transition-all hover:opacity-90 shadow-lg"
              onClick={() => setShowCreateModal(true)}
            >
              <User size={18} />
              {t('cashier.clients.newClient', 'New Client')}
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-surface text-foreground border border-border rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-surface-hover" onClick={handleExport}>
              <Download size={18} />
              {t('cashier.clients.export', 'Export')}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative bg-surface rounded-xl border border-border shadow-sm">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            placeholder={t('cashier.clients.searchPlaceholder', 'Search...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full py-3.5 pl-12 pr-4 border-none bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted rounded-xl"
          />
        </div>

        {/* Clients Table */}
        <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden flex flex-col min-h-[400px]">
        {isLoading ? (
          <div className="py-16 px-5 flex items-center justify-center">
            <LoadingState message={t('common.loading', 'Loading...')} />
          </div>
        ) : error ? (
          <div className="py-16 px-5 flex items-center justify-center text-ctp-red">
            <p>{t('common.error', 'An error occurred')}</p>
          </div>
        ) : (data?.data?.length ?? 0) === 0 ? (
          <div className="py-16 px-5 flex items-center justify-center">
            <EmptyState
              title={t('cashier.clients.empty.title', 'No clients yet')}
              description={t('cashier.clients.empty.description', 'Clients will appear here after transactions are created')}
            />
          </div>
        ) : (
          <div className="overflow-x-auto flex-1">
            <table className="table min-w-[600px]">
              <thead>
                <tr>
                  <th>{t('cashier.clients.table.id', 'ID')}</th>
                  <th>{t('cashier.clients.table.clientName', 'CLIENT NAME')}</th>
                  <th className="text-center">{t('cashier.clients.table.employee', 'EMPLOYEE')}</th>
                  <th className="text-right">{t('cashier.clients.table.transactions', 'TRANSACTIONS')}</th>
                  <th>{t('cashier.clients.table.regDate', 'FIRST SEEN')}</th>
                </tr>
              </thead>
              <tbody>
                {data?.data?.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="inline-block px-2.5 py-1.5 bg-ctp-blue/10 border border-ctp-blue/30 rounded-md text-xs font-semibold text-ctp-blue font-mono truncate max-w-[120px]" title={client.id}>{client.id.slice(0, 8)}...</span>
                        <button
                          className={`w-7 h-7 border border-border rounded-md bg-surface flex items-center justify-center transition-all hover:bg-surface-hover hover:text-foreground ${copiedId === client.id ? 'bg-ctp-green/10 border-ctp-green text-ctp-green' : 'text-foreground-muted'}`}
                          onClick={() => handleCopyId(client.id)}
                          title={t('cashier.clients.copyId', 'Copy ID')}
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-surface-hover flex items-center justify-center text-foreground-muted">
                          <User size={16} />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-medium">{client.name}</span>
                          <code className="text-xs text-foreground-muted bg-surface-hover px-1.5 py-0.5 rounded mt-0.5 w-fit">{client.uniqueId}</code>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      {client.isEmployee ? (
                        <span
                          className="inline-block text-[10px] uppercase tracking-wide bg-ctp-teal/15 text-ctp-teal rounded px-2 py-0.5 font-semibold"
                          title={t('cashier.clients.employee', 'Employee')}
                        >
                          {t('cashier.clients.employee', 'Employee')}
                        </span>
                      ) : (
                        <span className="text-foreground-muted">—</span>
                      )}
                    </td>
                    <td className="text-right font-semibold text-ctp-blue">
                      {client.transactionCount || 0}
                    </td>
                    <td className="text-foreground-muted whitespace-nowrap">{formatDate(client.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && (data?.data?.length ?? 0) > 0 && totalPages > 1 && (
          <div className="flex justify-between items-center px-5 py-4 border-t border-border flex-col md:flex-row gap-4">
            <span className="text-[13px] text-foreground-muted">
              {t('cashier.clients.page', 'Page')} {currentPage} {t('cashier.clients.of', 'of')} {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 border border-border rounded-lg bg-surface text-foreground-muted text-[13px] font-medium transition-all hover:bg-surface-hover hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                disabled={currentPage === 1}
                onClick={() => setFilters({ ...filters, page: currentPage - 1 })}
              >
                <ChevronLeft size={14} />
                {t('common.previous', 'Previous')}
              </button>
              <button
                className="px-3 py-1.5 border border-border rounded-lg bg-surface text-foreground-muted text-[13px] font-medium transition-all hover:bg-surface-hover hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                disabled={currentPage === totalPages}
                onClick={() => setFilters({ ...filters, page: currentPage + 1 })}
              >
                {t('common.next', 'Next')}
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Create Client Modal */}
    {showCreateModal && currentEntity && (
      <CreateClientModal
        entityId={currentEntity.id}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          setShowCreateModal(false);
        }}
      />
    )}
  </div>
  );
}

